/**
 * LiveToolRunner
 *
 * Renders the appropriate prioritization tool UI inline inside the live session
 * page — so participants never leave /live to do their ranking.
 *
 * The component lazy-loads the tool component by compatibilityId, feeds it the
 * session items via ToolItemsProvider, and wires onCompleteResults to persist
 * the participant's response to tool_session_responses.
 */

import { lazy, Suspense, useCallback, useMemo, useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { ToolItemsProvider } from '../../../contexts/ToolItemsContext';
import type { SessionReadout, ToolItem, ToolSituationalContext } from '../../../contexts/ToolItemsContext';
import { normalizeSessionToolId } from '../../../lib/sessionToolCatalog';
import { useToolSessionStore } from '../../../stores/prioritization/toolSessionStore';
import type { ToolHostProps, ToolResultItem } from '../ToolWrapper';
import { RationaleCaptureModal } from './RationaleCaptureModal';

// ─── Lazy tool registry ──────────────────────────────────────────────────────
// Each entry is a React.lazy import — only the tool that's actually needed for
// this session is loaded. Everything else stays out of the initial bundle.

const LAZY_TOOL_COMPONENTS: Record<string, React.LazyExoticComponent<React.ComponentType<ToolHostProps>>> = {
  eisenhower_matrix:       lazy(() => import('../../../pages/eisenhower-matrix/EisenhowerMatrix')),
  impact_effort_matrix:    lazy(() => import('../../../pages/impact-effort-matrix/ImpactEffortMatrix')),
  moscow:                  lazy(() => import('../../../pages/moscow-method/MoscowMethod')),
  dot_voting:              lazy(() => import('../../../pages/dot-voting/DotVoting')),
  pairwise_ranking:        lazy(() => import('../../../pages/pairwise-ranking/PairwiseRanking')),
  stack_ranking:           lazy(() => import('../../../pages/stack-ranking/StackRanking')),
  buy_a_feature:           lazy(() => import('../../../pages/buy-a-feature/BuyAFeature')),
  rice:                    lazy(() => import('../../../pages/rice-scoring/RiceScoring')),
  simple_weighted_scoring: lazy(() => import('../../../pages/simple-weighted-scoring/SimpleWeightedScoring')),
  wsjf:                    lazy(() => import('../../../pages/wsjf/WeightedSmallestJobFirst')),
  cost_of_delay:           lazy(() => import('../../../pages/cost-of-delay/CostOfDelay')),
  pareto_analysis:         lazy(() => import('../../../pages/pareto-analysis/ParetoAnalysis')),
  max_priorities_pyramid:  lazy(() => import('../../../pages/max-priorities-pyramid/MaxPrioritiesPyramid')),
  ahp:                     lazy(() => import('../../../pages/ahp/AHP')),
  fast_vs_right:           lazy(() => import('../../../pages/fast-vs-right/FastVsRight')),
  speed_boat:              lazy(() => import('../../../pages/speed-boat/SpeedBoat')),
  prune_product_tree:      lazy(() => import('../../../pages/prune-product-tree/PruneProductTree')),
  magic_prioritization:    lazy(() => import('../../../pages/magic-prioritization/MagicPrioritization')),
};

// ─── Props ───────────────────────────────────────────────────────────────────

export interface LiveToolRunnerProps {
  sessionId: string;
  toolSessionId: string;
  /** Required for tool_session_responses.workspace_id (NOT NULL) */
  workspaceId: string;
  /** session.selectedToolId — any alias or slug; normalised internally */
  selectedToolId: string | null | undefined;
  /** Items the facilitator loaded for this session */
  items: ToolItem[];
  participantId: string;
  participantName: string;
  /** Called after the participant's results are persisted */
  onComplete: () => void;
  /** Called when the participant wants to go back to the board without submitting */
  onBack: () => void;
  /** Called when persistence fails */
  onError?: (message: string) => void;
  /** When true, only render tool canvas (parent provides live session chrome) */
  embedded?: boolean;
  /**
   * Guest mode: persist through `submit_guest_response_with_token` (migration
   * 660) instead of the member-only `submitParticipantResponse` store action.
   * When provided, `workspaceId` is not required (the RPC resolves it
   * server-side from the token) and the rationale step is skipped — the guest
   * RPC has no rationale column, so a captured rationale would be silently
   * dropped rather than saved.
   */
  guestPersist?: (results: ToolResultItem[]) => Promise<void>;
  /** session.toolConfig — the settings chosen at setup (dots, budget, band). */
  toolConfig?: Record<string, unknown>;
  /** A guest's copy of what the screen reads from the session row (migration 964). */
  sessionReadout?: SessionReadout;
}

// ─── Fallback for unknown tools ──────────────────────────────────────────────

function UnknownToolFallback({ toolId, onBack }: { toolId: string; onBack: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center flex-1 gap-4 text-[var(--text-ter)] px-8 text-center">
      <p className="text-base font-medium text-[var(--text-ter)]">
        No interactive view for <span className="font-mono text-[var(--text-ter)]">{toolId}</span>
      </p>
      <p className="text-sm">
        Open the session's Run tab to use this tool, then return here when done.
      </p>
      <button
        onClick={onBack}
        className="text-sm text-[var(--text-ter)] underline hover:text-white mt-2"
      >
        Back to board
      </button>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

export function LiveToolRunner({
  sessionId,
  toolSessionId,
  workspaceId,
  selectedToolId,
  items,
  participantId,
  participantName,
  onComplete,
  onBack,
  onError,
  embedded = false,
  guestPersist,
  toolConfig,
  sessionReadout,
}: LiveToolRunnerProps) {
  const canonicalId = useMemo(
    () => normalizeSessionToolId(selectedToolId),
    [selectedToolId]
  );

  // Rationale policy for this tool session drives whether we prompt for a "why".
  // Guests never see the rationale step: the toolSessionStore is never
  // subscribed for an unauthenticated caller (RLS blocks the read), and even
  // if it were, submit_guest_response_with_token has no rationale parameter.
  const memberRationalePolicy = useToolSessionStore(
    s => s.toolSessions.find(ts => ts.id === toolSessionId)?.rationalePolicy ?? 'encouraged'
  );
  const rationalePolicy = guestPersist ? 'off' : memberRationalePolicy;
  const [pendingResults, setPendingResults] = useState<ToolResultItem[] | null>(null);
  const [submitting, setSubmitting] = useState(false);

  /**
   * F8 — one action, one button.
   *
   * When the session asks for a rationale, this control does not submit: it
   * opens RationaleCaptureModal, whose "Submit response" is the button that
   * actually records the response. Both said "Submit", so the participant's
   * first click appeared to do nothing — the submission counter stayed at
   * 0 of N until they found the second one
   * (docs/9-exploration/MOSSO-DEMO-FINDINGS-2026-08-25.md).
   *
   * So the footer button is now named for what it does. Exactly one control in
   * the participant's view is labelled Submit at any moment: this one when it
   * submits directly, the modal's when a rationale stands between.
   */
  const completeLabel = rationalePolicy === 'off' ? 'Submit my response' : 'Review & submit';

  const situationalContext = useMemo<ToolSituationalContext>(
    () => ({
      inputMode: 'preloaded',
      completion: {
        label: completeLabel,
        navigateOnComplete: false,
      },
      sessionId,
      activeToolSessionId: toolSessionId,
      currentUserId: participantId,
      currentUserName: participantName,
      toolConfig,
      sessionReadout,
    }),
    [sessionId, toolSessionId, participantId, participantName, completeLabel, toolConfig, sessionReadout],
  );

  const ToolComponent = canonicalId ? LAZY_TOOL_COMPONENTS[canonicalId] : undefined;

  const persistResponse = useCallback(
    async (results: ToolResultItem[], rationale?: string) => {
      if (guestPersist) {
        try {
          await guestPersist(results);
          onComplete();
        } catch (err) {
          const message = err instanceof Error ? err.message : 'Could not save your response.';
          onError?.(message);
          throw err;
        }
        return;
      }
      if (!workspaceId) {
        const message = 'Workspace context is missing — cannot save your response.';
        onError?.(message);
        throw new Error(message);
      }
      try {
        await useToolSessionStore.getState().submitParticipantResponse({
          toolSessionId,
          workspaceId,
          participantId,
          participantName,
          results,
          rationale,
        });
        onComplete();
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Could not save your response.';
        onError?.(message);
        throw err;
      }
    },
    [toolSessionId, workspaceId, participantId, participantName, onComplete, onError, guestPersist]
  );

  const handleCompleteResults = useCallback(
    async (results: ToolResultItem[]) => {
      if (results.length === 0) {
        const message = 'Finish rating all items before submitting.';
        onError?.(message);
        throw new Error(message);
      }
      // When the session asks for rationale, capture it first (never blocking).
      if (rationalePolicy !== 'off') {
        setPendingResults(results);
        return;
      }
      await persistResponse(results);
    },
    [rationalePolicy, persistResponse, onError]
  );

  const handleRationaleSubmit = useCallback(
    async (rationale: string) => {
      if (!pendingResults) return;
      setSubmitting(true);
      try {
        await persistResponse(pendingResults, rationale || undefined);
        setPendingResults(null);
      } catch {
        // error already surfaced via onError; keep modal open for retry
      } finally {
        setSubmitting(false);
      }
    },
    [pendingResults, persistResponse]
  );

  // The outer page shell (LiveSessionPage) guarantees items.length > 0 before
  // mounting LiveToolRunner, so ToolWrapper can never fall back to workspace items.
  const toolCanvas = ToolComponent ? (
    <Suspense
      fallback={
        <div className="flex items-center justify-center h-full gap-3 text-[var(--text-ter)]">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-sm font-body">Loading tool…</span>
        </div>
      }
    >
      <ToolItemsProvider
        items={items}
        hostContext="session"
        situationalContext={situationalContext}
      >
        <ToolComponent
          hostContext="session"
          displayMode="page"
          onCompleteResults={handleCompleteResults}
          completeActionLabel={completeLabel}
          backTo={embedded ? undefined : { label: '← Back to board', onClick: onBack }}
        />
      </ToolItemsProvider>
    </Suspense>
  ) : (
    <UnknownToolFallback toolId={selectedToolId ?? 'unknown'} onBack={onBack} />
  );

  const rationaleModal =
    pendingResults && rationalePolicy !== 'off' ? (
      <RationaleCaptureModal
        policy={rationalePolicy}
        isSubmitting={submitting}
        onSubmit={handleRationaleSubmit}
        onCancel={() => setPendingResults(null)}
      />
    ) : null;

  if (embedded) {
    return (
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {toolCanvas}
        {rationaleModal}
      </div>
    );
  }

  return (
    <div className="fixed inset-y-0 left-64 right-0 z-40 flex flex-col bg-[var(--bg)] text-[var(--text-pri)]">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--rule)] bg-[var(--bg-raised)] flex-shrink-0">
        <button
          type="button"
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to board
        </button>
        <div className="flex-1" />
        <span className="text-xs text-[var(--text-ter)] font-body">Submit your response when you&apos;re done</span>
      </div>
      <div className="flex-1 min-w-0 overflow-auto">{toolCanvas}</div>
      {rationaleModal}
    </div>
  );
}
